/**
 * AI 에이전트를 지금 쓸 수 있나 — 빌드 스위치(config/assistant) + 스탠드얼론은 **실행 중 확인**.
 *
 * 웹판: 빌드 스위치 그대로 (어시스턴트는 QBase 쪽 공통 서비스, nginx 가 /assistant 를 넘긴다).
 * 스탠드얼론(무설치판): BE1 이 어시스턴트를 함께 띄운다. 내부망이면 외부 LLM 에 닿지 않으므로
 * 서버(/api/assistant/status)가 인터넷 연결을 보고 알려 준다 → 안 되면 AI 버튼·창·AI 제안을 감춘다.
 * 이유(reason)는 도움말 → 정보에 보인다: off(설정으로 끔) · noService(에이전트 미동작) · offline(인터넷 없음).
 */
import { useEffect, useState } from 'react';
import { ASSISTANT_ENABLED } from '../config/assistant';
import { IS_LOCAL } from '../config/mode';
import { BE1_API } from '../config/api';

export interface AssistantStatus { enabled: boolean; reason: 'off' | 'noService' | 'offline' | null }

const OFF: AssistantStatus = { enabled: false, reason: 'off' };
let cached: AssistantStatus | null = !ASSISTANT_ENABLED ? OFF : !IS_LOCAL ? { enabled: true, reason: null } : null;
let pending: Promise<AssistantStatus> | null = null;
const listeners = new Set<(s: AssistantStatus) => void>();

/** 서버에 한 번 묻고 기억한다. 로그인 전(401)이거나 실패하면 기억하지 않는다 — 다음에 다시 묻는다 */
export function loadAssistantStatus(): Promise<AssistantStatus> {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = fetch(`${BE1_API}/api/assistant/status`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) return { enabled: false, reason: 'noService' as const };
        const b = await r.json();
        const s: AssistantStatus = { enabled: b?.enabled === true, reason: b?.reason ?? null };
        cached = s;
        listeners.forEach((f) => f(s));
        return s;
      })
      .catch(() => ({ enabled: false, reason: 'noService' as const }))
      .finally(() => { pending = null; });
  }
  return pending;
}

export function useAssistantStatus(): AssistantStatus {
  const [s, setS] = useState<AssistantStatus>(cached || { enabled: false, reason: null });
  useEffect(() => {
    listeners.add(setS);
    if (!cached) loadAssistantStatus().then(setS);
    return () => { listeners.delete(setS); };
  }, []);
  return s;
}

/** AI 기능을 보일지 */
export function useAssistantEnabled(): boolean {
  return useAssistantStatus().enabled;
}
